import { z } from 'zod'
import type { PaginatedResponse } from './common'

export const campaignTypeSchema = z.string().trim().min(2).max(80)
export type CampaignType = z.infer<typeof campaignTypeSchema>

const campaignBaseSchema = z.object({
  campaign_type: campaignTypeSchema,
  title_en: z.string().min(3).max(200),
  title_hi: z.string().min(3).max(200),
  description_en: z.string().max(10000).optional(),
  description_hi: z.string().max(10000).optional(),
  goal_amount_paise: z.number().int().nonnegative().nullable().optional(),
  start_date: z
    .string()
    .datetime()
    .nullable()
    .optional()
    .refine(
      val => {
        if (!val) return true // null/undefined is fine
        const todayStart = new Date()
        todayStart.setUTCHours(0, 0, 0, 0)
        return new Date(val).getTime() >= todayStart.getTime()
      },
      {
        message:
          'Session date cannot be in the past. Please select today or a future date.',
      },
    ),
  end_date: z.string().datetime().nullable().optional(),
  thumbnail_url: z.string().url().optional(),
  gallery: z.array(z.string().url()).optional().default([]),
  is_active: z.boolean().default(true),
})

export const createCampaignSchema = campaignBaseSchema
export const updateCampaignSchema = campaignBaseSchema.partial()

export type CreateCampaignInput = z.infer<typeof createCampaignSchema>
export type UpdateCampaignInput = z.infer<typeof updateCampaignSchema>

export type CampaignResponse = {
  id: string
  legacy_id?: string | null
  campaign_type: CampaignType
  title_en: string
  title_hi: string
  description_en?: string | null
  description_hi?: string | null
  goal_amount_paise?: number | null
  raised_amount_paise: number
  start_date?: string | null
  end_date?: string | null
  thumbnail_url?: string | null
  gallery: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export type CampaignSortBy = 'created_at' | 'title_en' | 'goal_amount_paise'

export type PaginatedCampaignResponse = PaginatedResponse<CampaignResponse>
